import {AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy, Renderer2} from '@angular/core';
import {fromEvent, Observable, Subject} from 'rxjs';
import {filter, takeUntil} from 'rxjs/operators';
import {ICoordinate} from './directives.helper';

@Directive({ selector: '[insDraggable]' })
export class DraggableDirective implements AfterViewInit, OnDestroy {

  // Element to be dragged
  private htmlElParent: HTMLElement = null;
  private delta: ICoordinate = {x: 0, y: 0};
  private readonly offset: ICoordinate = {x: 0, y: 0};
  private readonly epsilon: ICoordinate = {x: 0, y: 0};

  private elemRectRight: number = 0;
  private elemRectBottom: number = 0;

  private readonly destroy$: Subject<void> = new Subject<void>();
  private elemRect: DOMRect;

  @Input() limitMovement: boolean = true;

  constructor(private readonly elementRef: ElementRef, private readonly zone: NgZone, private readonly renderer: Renderer2) {
    this.renderer.setStyle(elementRef.nativeElement, 'cursor', 'move');
  }

  private fillParentElement(): void {
    this.htmlElParent = this.elementRef.nativeElement.parentElement.parentElement;
    this.elemRect = this.htmlElParent.getBoundingClientRect();

    // we will work only with INT (no decimal point) due to Blur Bug when using Translate animation!
    // The Blur effect is a known bug of "Transform: translate"
    this.elemRectRight = Math.round(this.elemRect.right);
    this.epsilon.x = this.elemRect.right - this.elemRectRight;
    this.elemRectBottom = Math.round(this.elemRect.bottom);
    this.epsilon.y = this.elemRect.bottom - this.elemRectBottom;

    this.translate();
  }

  private setupEvents(): void {
    this.zone.runOutsideAngular(() => {
      const el: HTMLElement = this.elementRef.nativeElement;
      const pointerDown$: Observable<Event> = fromEvent(el, 'pointerdown');
      const pointerMove$: Observable<Event> = fromEvent(el, 'pointermove');
      const pointerUp$: Observable<Event> = fromEvent(el, 'pointerup');
      const pointerCancel$: Observable<Event> = fromEvent(el, 'pointercancel');

      let isDragging = false;
      let startX = 0;
      let startY = 0;

      const endDrag = (): void => {
        if (!isDragging) {
          return;
        }
        isDragging = false;
        this.offset.x += this.delta.x;
        this.offset.y += this.delta.y;
        this.delta = {x: 0, y: 0};
        document.body.style.userSelect = '';
      };

      // Walk up from the event target to the drag handle element.
      // If any ancestor has [data-no-drag], class 'dialogCloseIcon', or is a native interactive element, skip drag.
      // Note: mat-icon with svgIcon renders <svg>/<path> children, so we walk up to find the mat-icon ancestor.
      const isInteractiveTarget = (target: EventTarget): boolean => {
        let node = target as HTMLElement;
        while (node && node !== el) {
          if (['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA'].includes(node.tagName)) {
            return true;
          }
          if (node.hasAttribute('data-no-drag')) {
            return true;
          }
          if (node.classList && node.classList.contains('dialogCloseIcon')) {
            return true;
          }
          node = node.parentElement;
        }
        return false;
      };

      pointerDown$.pipe(
        filter((downEvent: PointerEvent) => downEvent.button === 0),
        takeUntil(this.destroy$)
      ).subscribe((downEvent: PointerEvent) => {
        if (isInteractiveTarget(downEvent.target)) {
          return;
        }
        if (!this.htmlElParent) {
          this.fillParentElement();
        }
        // Capture all pointer events to this element for the duration of the drag,
        // so pointermove fires even when the cursor moves outside the header or dialog.
        el.setPointerCapture(downEvent.pointerId);
        isDragging = true;
        startX = downEvent.clientX;
        startY = downEvent.clientY;
        document.body.style.userSelect = 'none';
      });

      pointerMove$.pipe(takeUntil(this.destroy$)).subscribe((moveEvent: PointerEvent) => {
        if (!isDragging) {
          return;
        }
        moveEvent.preventDefault();
        this.delta = {
          x: moveEvent.clientX - startX,
          y: moveEvent.clientY - startY
        };
        if (this.delta.x === 0 && this.delta.y === 0) {
          return;
        }
        if (this.limitMovement) {
          this.applyLimitMovement();
        }
        this.translate();
      });

      pointerUp$.pipe(takeUntil(this.destroy$)).subscribe(endDrag);
      pointerCancel$.pipe(takeUntil(this.destroy$)).subscribe(endDrag);
    });
  }

  private translate(): void {
    requestAnimationFrame(() => {
      // fixed property dosen't work inside translate. SO all the dropdown with fixed position are not rendering correctly when used on 
      // modal and modal is dragged from its original position
      // this.htmlElParent.style.transform = `translate(${this.offset.x + this.delta.x}px, ${this.offset.y + this.delta.y}px)`;
      this.htmlElParent.style.left = this.offset.x + this.delta.x + 'px';
      this.htmlElParent.style.top = this.offset.y + this.delta.y + 'px';
    });
  }

  // applyLimitMovement - limit draggable element movement to within the monitor's boundaries
  private applyLimitMovement(): void {
    const currBBRect = this.htmlElParent.getBoundingClientRect();

    // Watch left and right boundaries
    if (currBBRect.left <= 0 && (this.offset.x + this.delta.x) <= -this.elemRect.left) {
      this.delta.x = (-this.elemRect.left - this.offset.x);
    } else if ((currBBRect.right - this.epsilon.x >= window.innerWidth)
      && ((this.offset.x + this.delta.x + this.elemRectRight) >= window.innerWidth)) {
      this.delta.x = window.innerWidth - this.offset.x - this.elemRectRight;
    }

    // Watch top and bottom boundaries
    if (currBBRect.top <= 0 && (this.offset.y + this.delta.y) <= -this.elemRect.top) {
      this.delta.y = (-this.elemRect.top - this.offset.y);
    } else if ((currBBRect.bottom - this.epsilon.y >= window.innerHeight)
      && ((this.offset.y + this.delta.y + this.elemRectBottom) >= window.innerHeight)) {
      this.delta.y = window.innerHeight - this.offset.y -  this.elemRectBottom;
    }
  }

  public ngAfterViewInit(): void {
    this.setupEvents();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }
}
